// Sprint 3 Story 5: Wrapper-Card mit Header, Größen-Toggle, Entfernen-Button.

import { useState } from "preact/hooks";
import { ChevronDown, GripVertical, X } from "lucide-preact";
import type { DashboardWidget, DashboardWidgetSize } from "@juuplan/shared";
import { SIZE_LABELS } from "../../lib/dashboardLayout";

interface Props {
  widget: DashboardWidget;
  title: string;
  children: preact.ComponentChildren;
  onResize: (size: DashboardWidgetSize) => void;
  onRemove: () => void;
  /** Bearbeiten-Modus: zeigt Drag-Handle + Optionsmenü (Größe/Entfernen). */
  editing?: boolean;
  /** dragHandleProps von @hello-pangea/dnd — wird auf den Griff gelegt. */
  dragHandleProps?: Record<string, unknown>;
}

const SIZES: DashboardWidgetSize[] = ["small", "half", "wide", "full"];

export function WidgetCard(props: Props): preact.JSX.Element {
  const { widget, title, children, onResize, onRemove, editing, dragHandleProps } = props;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <article
      class={`juuplan-widget-card relative flex flex-col rounded-lg border bg-[color:var(--color-surface-container-lowest)] ${
        editing
          ? "border-[color:var(--color-primary)]/40 shadow-level-1"
          : "border-[color:var(--color-outline-variant)]"
      }`}
      data-widget-id={widget.id}
    >
      <header class="flex items-center gap-2 px-4 py-2.5 border-b border-[color:var(--color-outline-variant)]/60">
        {editing && (
          <span
            {...(dragHandleProps ?? {})}
            class="shrink-0 -ml-1 cursor-grab active:cursor-grabbing rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-label="Widget verschieben"
            title="Zum Verschieben ziehen"
          >
            <GripVertical size={16} aria-hidden="true" />
          </span>
        )}
        <h3 class="flex-1 min-w-0 truncate text-sm font-semibold text-[color:var(--color-on-surface)]">
          {title}
        </h3>
        <div class={`relative shrink-0 ${editing ? "" : "hidden"}`}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            class="inline-flex items-center gap-0.5 rounded p-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Widget-Optionen (Größe: ${SIZE_LABELS[widget.size]})`}
            title={`Größe: ${SIZE_LABELS[widget.size]}`}
          >
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {menuOpen && (
            <>
              <div
                class="fixed inset-0 z-30"
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
              />
              <div
                role="menu"
                class="absolute right-0 mt-1 z-40 w-40 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 py-1 text-xs"
              >
                <div class="px-3 py-1 text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                  Größe
                </div>
                {SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="menuitemradio"
                    aria-checked={s === widget.size}
                    onClick={() => {
                      onResize(s);
                      setMenuOpen(false);
                    }}
                    class={`w-full text-left px-3 py-1.5 hover:bg-[color:var(--color-surface-container-low)] ${
                      s === widget.size
                        ? "text-[color:var(--color-primary)] font-medium"
                        : "text-[color:var(--color-on-surface)]"
                    }`}
                  >
                    {SIZE_LABELS[s]}
                  </button>
                ))}
                <div class="border-t border-[color:var(--color-outline-variant)] my-1" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onRemove();
                    setMenuOpen(false);
                  }}
                  class="w-full text-left px-3 py-1.5 hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] text-[color:var(--color-error)] flex items-center gap-1.5"
                >
                  <X size={14} aria-hidden="true" />
                  Entfernen
                </button>
              </div>
            </>
          )}
        </div>
      </header>
      <div class="flex-1 min-h-0 overflow-auto rounded-b-lg p-4">{children}</div>
    </article>
  );
}
